import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, InputNumber, Checkbox, DatePicker, Button, Tabs, Modal, Tooltip } from 'antd';
import { EnvelopeSimple, Eye, EyeSlash, House, Info, Phone, Trash, User } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import contractService from '../../../../services/oneButtonServices/contractService';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import './oneButton-css/GoodLeapShareLoanModal.css';
import './oneButton-css/OneButtonFormControls.css';
import './oneButton-css/ServiceFinanceSubmitModal.css';

/** Trinity `GenerateContractContant` option lists. */
const RESIDENCE = [
    { value: 'Own', label: 'Own' },
    { value: 'Rent', label: 'Rent' },
];
const PROPERTY_TYPES = [
    { value: 'Single Family', label: 'Single Family' },
    { value: 'Manufactured Home', label: 'Manufactured Home' },
];
const DOCUMENT_TYPES = [
    { value: 'Passport', label: 'Passport' },
    { value: 'Driver License', label: 'Driver License' },
    { value: 'State ID', label: 'State ID' },
    { value: 'Military ID', label: 'Military ID' },
    { value: 'Visa', label: 'Visa' },
    { value: 'Green Card', label: 'Green Card' },
];
const CITIZEN = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

/** Trinity `GenerateContractContant.disclosurePoints` — rendered with `innerHTML`. */
const DISCLOSURE_POINTS = [
    '<p>1. All information and statements made in this application are true, complete, and correct to the best of my knowledge.</p>',
    '<p>2. I/We authorize Service Finance Company, LLC and its affiliates to verify the information provided and to obtain consumer credit reports.</p>',
    '<p>3. I/We understand that submitting this application does not guarantee credit approval or specific terms.</p>',
    '<p>4. I/We agree that Service Finance Company, LLC may contact me/us at the telephone numbers provided, including by autodialed or prerecorded calls or texts.</p>',
];

const INCOME_DISCLOSURE =
    'Alimony, child support, or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repaying this obligation.';

export type ServiceFinanceSubmitModalRow = {
    quoteIdRaw: string;
    totalEpcDisplay: string;
    termRaw?: string;
    termAprRaw?: string;
    productTypeCode?: string;
    productType?: string;
    financePartnerRaw?: string;
    financePartner?: string;
    financeMethod?: string;
    financePartnerContractIdRaw?: string;
    opportunityContractIdRaw?: string;
    contractIdRaw?: string;
    primaryContactDisplay?: string;
    contractStatus?: string;
};

type ApplicantValues = {
    dob?: unknown;
    ssn?: string;
    employerName?: string;
    grossMonthIncome?: number;
    residence?: string;
    mortgageRentalAmount?: number;
    propertyType?: string;
    iD_Type?: string;
    documentNumber?: string;
    state?: string;
    issueDate?: unknown;
    expirationDate?: unknown;
    issuer?: string;
    uS_CitizenShip?: string;
    phoneType?: string;
    phoneNumber?: string;
};

type PhoneTypeOpt = { Text: string; Value?: string | number };
type SfCustomer = {
    CustomerId?: string | number;
    FirstName?: string;
    LastName?: string;
    Phone?: string;
    Email?: string;
    PhoneType?: string | number;
    IsPrimaryContact?: boolean;
    InsStreet?: string;
    InsCity?: string;
    InsState?: string;
    InsZipCode?: string;
};

type SidebarInfo = {
    OpportunityDetails: { Name?: string; Phone?: string; Email?: string };
    InstallationAddress?: { Street?: string; City?: string; State?: string; ZipCode?: string };
    SalesPerson?: { Sales_Division?: string; Name?: string; Phone?: string; Email?: string };
};

function opportunityDetailsRoot(raw: any): Record<string, unknown> | null {
    if (!raw || typeof raw !== 'object') return null;
    const o =
        raw.OpportunityDetails ??
        raw.opportunityDetails ??
        raw.Opportunityinfo?.OpportunityDetails ??
        raw.OpportunityInfo?.OpportunityDetails;
    return (o as Record<string, unknown>) || null;
}

function installationAddressRoot(raw: any): Record<string, unknown> | null {
    if (!raw || typeof raw !== 'object') return null;
    const a =
        raw.InstallationAddress ??
        raw.installationAddress ??
        raw.Opportunityinfo?.InstallationAddress ??
        raw.OpportunityInfo?.InstallationAddress;
    return (a as Record<string, unknown>) || null;
}

function salesPersonRoot(raw: any): Record<string, unknown> | null {
    if (!raw || typeof raw !== 'object') return null;
    const od = opportunityDetailsRoot(raw);
    const sp =
        raw.SalesPerson ??
        raw.salesPerson ??
        od?.SalesPerson ??
        od?.salesPerson ??
        raw.Opportunityinfo?.SalesPerson ??
        raw.OpportunityInfo?.SalesPerson;
    return (sp as Record<string, unknown>) || null;
}

function formatAddrStateSegment(state: string | undefined): string {
    const s = (state || '').trim();
    if (!s) return '';
    if (s.length <= 2) return s.toUpperCase();
    return s
        .toLowerCase()
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

function formatSsnDigits(raw: string): string {
    let value = raw.replace(/\D/g, '').slice(0, 9);
    if (value.length > 3) value = `${value.slice(0, 3)}-${value.slice(3)}`;
    if (value.length > 6) value = `${value.slice(0, 6)}-${value.slice(6)}`;
    return value;
}

function formatPhoneDigits(raw: string): string {
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    if (!digits) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function parseMoney(raw: string | number | null | undefined): number {
    const n = parseFloat(String(raw ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
}

function formatDate(v: unknown): string {
    return v ? dayjs(v as any).format('MM/DD/YYYY') : '';
}

function formatTermAndApr(row: ServiceFinanceSubmitModalRow | null): string {
    const term = String(row?.termRaw ?? '').trim();
    const apr = String(row?.termAprRaw ?? '').trim();
    if (!term || !apr) return '';
    return `${term} years at ${apr}%`;
}

function productTypeForProgram(row: ServiceFinanceSubmitModalRow | null): string {
    const pt = String(row?.productTypeCode || row?.productType || '').trim();
    return pt === 'RR' ? 'R&R' : 'R';
}

function pickCustomers(opportunityDetails: any): SfCustomer[] {
    const list =
        opportunityDetails?.Customers ??
        opportunityDetails?.customers ??
        opportunityDetailsRoot(opportunityDetails)?.Customers ??
        opportunityDetailsRoot(opportunityDetails)?.customers;
    return Array.isArray(list) ? list : [];
}

function buildPrimarySidebar(opportunityDetails: any): SidebarInfo {
    const od = opportunityDetailsRoot(opportunityDetails) || {};
    const inst = installationAddressRoot(opportunityDetails);
    const sp = salesPersonRoot(opportunityDetails);
    const primary = pickCustomers(opportunityDetails).find((c) => c.IsPrimaryContact) ?? pickCustomers(opportunityDetails)[0];
    const name =
        String(od.Name ?? od.name ?? '').trim() ||
        [primary?.FirstName, primary?.LastName].filter(Boolean).join(' ').trim();
    return {
        OpportunityDetails: {
            Name: name,
            Phone: String(od.Phone ?? od.phone ?? primary?.Phone ?? '').trim(),
            Email: String(od.Email ?? od.email ?? primary?.Email ?? '').trim(),
        },
        InstallationAddress: inst
            ? {
                  Street: String(inst.Street ?? inst.street ?? ''),
                  City: String(inst.City ?? inst.city ?? ''),
                  State: formatAddrStateSegment(String(inst.State ?? inst.state ?? '')),
                  ZipCode: String(inst.ZipCode ?? inst.zipCode ?? ''),
              }
            : undefined,
        SalesPerson: sp
            ? {
                  Sales_Division: String(sp.Sales_Division ?? sp.sales_Division ?? ''),
                  Name: String(sp.Name ?? sp.name ?? ''),
                  Phone: String(sp.Phone ?? sp.phone ?? ''),
                  Email: String(sp.Email ?? sp.email ?? ''),
              }
            : undefined,
    };
}

function applicantReady(values: ApplicantValues | undefined, includeMortgage: boolean): boolean {
    if (!values) return false;
    const keys: (keyof ApplicantValues)[] = [
        'dob',
        'ssn',
        'employerName',
        'residence',
        'propertyType',
        'iD_Type',
        'documentNumber',
        'state',
        'issueDate',
        'expirationDate',
        'issuer',
        'uS_CitizenShip',
        'phoneType',
        'phoneNumber',
    ];
    if (includeMortgage) keys.push('mortgageRentalAmount');
    for (const k of keys) {
        const v = values[k];
        if (v == null || v === '') return false;
    }
    if (values.grossMonthIncome == null) return false;
    if (!/^\d{3}-\d{2}-\d{4}$/.test(String(values.ssn || ''))) return false;
    if (String(values.phoneNumber || '').length < 14) return false;
    return true;
}

function SidebarBlock({ info }: { info: SidebarInfo }) {
    const od = info.OpportunityDetails;
    const inst = info.InstallationAddress;
    const sp = info.SalesPerson;
    return (
        <div className="obd-gl-share__sidebar obd-sf-submit__sidebar">
            <section className="obd-gl-share__sidebar-section">
                {od.Name ? (
                    <div className="obd-gl-share__line">
                        <span className="obd-gl-share__dot">
                            <User size={14} weight="duotone" />
                        </span>
                        <span>{od.Name}</span>
                    </div>
                ) : null}
                {od.Phone ? (
                    <div className="obd-gl-share__line">
                        <span className="obd-gl-share__dot">
                            <Phone size={14} weight="duotone" />
                        </span>
                        <span>{od.Phone}</span>
                    </div>
                ) : null}
                {od.Email ? (
                    <div className="obd-gl-share__line">
                        <span className="obd-gl-share__dot">
                            <EnvelopeSimple size={14} weight="duotone" />
                        </span>
                        <span className="obd-gl-share__email-text">{od.Email}</span>
                    </div>
                ) : null}
                {inst ? (
                    <div className="obd-gl-share__line">
                        <span className="obd-gl-share__dot">
                            <House size={14} weight="duotone" />
                        </span>
                        <span className="obd-gl-share__addr">
                            {[inst.Street, inst.City, inst.State, inst.ZipCode].filter(Boolean).join(' ')}
                        </span>
                    </div>
                ) : null}
            </section>
            {sp ? (
                <section className="obd-gl-share__sidebar-section obd-gl-share__sales">
                    <div className="obd-gl-share__sales-head">Sales Person: {sp.Sales_Division}</div>
                    {sp.Name ? (
                        <div className="obd-gl-share__line">
                            <span className="obd-gl-share__dot">
                                <User size={14} weight="duotone" />
                            </span>
                            <span>{sp.Name}</span>
                        </div>
                    ) : null}
                    {sp.Phone ? (
                        <div className="obd-gl-share__line">
                            <span className="obd-gl-share__dot">
                                <Phone size={14} weight="duotone" />
                            </span>
                            <span>{sp.Phone}</span>
                        </div>
                    ) : null}
                    {sp.Email ? (
                        <div className="obd-gl-share__line">
                            <span className="obd-gl-share__dot">
                                <EnvelopeSimple size={14} weight="duotone" />
                            </span>
                            <span className="obd-gl-share__email-text">{sp.Email}</span>
                        </div>
                    ) : null}
                </section>
            ) : null}
        </div>
    );
}

function SsnField({
    name,
    show,
    onToggle,
}: {
    name: string;
    show: boolean;
    onToggle: () => void;
}) {
    return (
        <Form.Item
            label="Social Security Number"
            name={name}
            required
            getValueFromEvent={(e) => formatSsnDigits(e.target.value)}
            rules={[
                { required: true, message: 'Required' },
                { pattern: /^\d{3}-\d{2}-\d{4}$/, message: 'Invalid SSN format. Please use XXX-XX-XXXX.' },
            ]}
        >
            <div className="social-security-number">
                <Input
                    type={show ? 'text' : 'password'}
                    maxLength={11}
                    autoComplete="off"
                    suffix={
                        <button type="button" className="obd-sf-submit__ssn-toggle" onClick={onToggle} aria-label={show ? 'Hide SSN' : 'Show SSN'}>
                            {show ? <EyeSlash size={14} /> : <Eye size={14} />}
                        </button>
                    }
                />
            </div>
        </Form.Item>
    );
}

function IncomeLabel() {
    return (
        <span className="obd-sf-submit__income-label">
            Gross Monthly Income
            <Tooltip title={INCOME_DISCLOSURE} overlayClassName="obd-sf-submit__disclosure-tip">
                <Info size={14} className="obd-agreements-modal__status-icon--primary" aria-label="Income disclosure" />
            </Tooltip>
        </span>
    );
}

/**
 * Trinity `ServiceFinanceApplicationSubmissionModalComponent` — primary + optional co-applicant,
 * Excel program type, disclosure checkbox, thank-you / Check Status screens.
 */
export function ServiceFinanceSubmitModal({
    open,
    onClose,
    row,
    opportunityId,
    opportunityDetails,
    salesPersonEmail,
    onCompleted,
    theme: themeProp = 'light',
}: {
    open: boolean;
    onClose: () => void;
    row: ServiceFinanceSubmitModalRow | null;
    opportunityId: string | number | null | undefined;
    opportunityDetails?: unknown;
    salesPersonEmail: string;
    onCompleted: (appId?: string) => void;
    theme?: 'dark' | 'light';
}) {
    const modalTheme: 'dark' | 'light' = themeProp === 'dark' ? 'dark' : 'light';
    const [primaryForm] = Form.useForm<ApplicantValues>();
    const [coForm] = Form.useForm<ApplicantValues>();
    const primaryValues = Form.useWatch([], primaryForm);
    const coValues = Form.useWatch([], coForm);

    const [loading, setLoading] = useState(false);
    const [acceptDeclaration, setAcceptDeclaration] = useState(false);
    const [appId, setAppId] = useState<string>('');
    const [showCurrentApplicationStatus, setShowCurrentApplicationStatus] = useState(false);
    const [contractStatus, setContractStatus] = useState('');
    const [primaryContact, setPrimaryContact] = useState('');
    const [modalTitle, setModalTitle] = useState('Submit Application to Service Finance');
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [showCoApplicantTab, setShowCoApplicantTab] = useState(false);
    const [selectedContact, setSelectedContact] = useState<string>('');
    const [showSsn, setShowSsn] = useState(false);
    const [showSsnCo, setShowSsnCo] = useState(false);
    const [phoneTypes, setPhoneTypes] = useState<PhoneTypeOpt[]>([]);
    const [programTypeValue, setProgramTypeValue] = useState('');
    const [coApplicantSidebar, setCoApplicantSidebar] = useState<SidebarInfo | null>(null);

    const opportunityinfoMasterData = useOneButtonStore((s) => s.opportunityinfoMasterData);
    const customers = useMemo(() => pickCustomers(opportunityDetails), [opportunityDetails]);
    const nonPrimaryContacts = useMemo(() => customers.filter((c) => c.IsPrimaryContact === false), [customers]);
    const primarySidebar = useMemo(() => buildPrimarySidebar(opportunityDetails), [opportunityDetails]);
    const sidebar = selectedTabIndex === 1 && coApplicantSidebar ? coApplicantSidebar : primarySidebar;

    const phoneTypeOptions = useMemo(
        () => phoneTypes.map((t) => ({ value: t.Text, label: t.Text })),
        [phoneTypes],
    );

    const resetState = useCallback(() => {
        primaryForm.resetFields();
        coForm.resetFields();
        setAcceptDeclaration(false);
        setShowCoApplicantTab(false);
        setSelectedContact('');
        setSelectedTabIndex(0);
        setShowSsn(false);
        setShowSsnCo(false);
        setShowCurrentApplicationStatus(false);
        setContractStatus('');
        setPrimaryContact('');
        setModalTitle('Submit Application to Service Finance');
        setCoApplicantSidebar(null);
        setProgramTypeValue('');
    }, [primaryForm, coForm]);

    const resolvePhoneTypeText = useCallback(
        (phoneTypeRaw: string | number | undefined) => {
            if (phoneTypeRaw == null || phoneTypeRaw === '') return '';
            const hit = phoneTypes.find((t) => String(t.Value) === String(phoneTypeRaw));
            return hit?.Text || '';
        },
        [phoneTypes],
    );

    useEffect(() => {
        if (!open) return;
        resetState();
        const existing = String(row?.financePartnerContractIdRaw ?? '').trim();
        setAppId(existing);

        const oppPhone = String(primarySidebar.OpportunityDetails.Phone || '').trim();
        primaryForm.setFieldsValue({
            grossMonthIncome: 0,
            mortgageRentalAmount: 0,
            uS_CitizenShip: 'true',
            phoneNumber: oppPhone ? formatPhoneDigits(oppPhone) : '',
        });
    }, [open, row?.quoteIdRaw, row?.financePartnerContractIdRaw, resetState, primaryForm, primarySidebar.OpportunityDetails.Phone]);

    useEffect(() => {
        if (!open) return;
        const masterPhones = opportunityinfoMasterData?.PhoneType || opportunityinfoMasterData?.phoneType;
        if (Array.isArray(masterPhones)) {
            setPhoneTypes(masterPhones.filter((ele: any) => ele.Text === 'Mobile' || ele.Text === 'Work'));
            return;
        }
        void masterServices
            .GetMasterDataByStep({ step: 1, paramDTOs: [{ key: 'string', value: 'string' }] })
            .then((res: any) => {
                const data = res?.data || res;
                const list = data?.PhoneType || data?.phoneType || [];
                setPhoneTypes((Array.isArray(list) ? list : []).filter((ele: any) => ele.Text === 'Mobile' || ele.Text === 'Work'));
            })
            .catch(() => undefined);
    }, [open, opportunityinfoMasterData]);

    useEffect(() => {
        if (!open || !phoneTypes.length) return;
        const primary = customers.find((c) => c.IsPrimaryContact);
        const text = resolvePhoneTypeText(primary?.PhoneType);
        if (text) primaryForm.setFieldsValue({ phoneType: text });
    }, [open, phoneTypes, customers, resolvePhoneTypeText, primaryForm]);

    useEffect(() => {
        if (!open || !row) return;
        const termApr = formatTermAndApr(row);
        if (!termApr) return;
        const inst = installationAddressRoot(opportunityDetails);
        const stateCode = String(inst?.stateCode ?? inst?.State ?? inst?.state ?? '').trim();
        void masterServices
            .cascadingDropDown({
                ExcelFileName: 'Sunnova Financial Data',
                SheetName: 'SVA Financing Options',
                InputValues: [
                    { Type: 'String', CellNumber: 'B4', CellValue: stateCode },
                    { Type: 'String', CellNumber: 'C4', CellValue: productTypeForProgram(row) },
                    { Type: 'String', CellNumber: 'D4', CellValue: String(row.financePartnerRaw || row.financePartner || '').trim() },
                    { Type: 'String', CellNumber: 'F4', CellValue: String(row.financeMethod || '').trim() },
                    { Type: 'String', CellNumber: 'K4', CellValue: '' },
                    { Type: 'String', CellNumber: 'M4', CellValue: '' },
                    { Type: 'String', CellNumber: 'O4', CellValue: termApr },
                    { Type: 'String', CellNumber: 'C8', CellValue: '' },
                ],
                OutputCells: ['T4', 'U4', 'V4', 'W4', 'X4', 'Y4', 'Z4', 'AA4', 'AB4'],
            })
            .then((out: any) => {
                const cell = Array.isArray(out) ? out[1] : null;
                setProgramTypeValue(String(cell?.CellValue ?? cell?.cellValue ?? '').trim());
            })
            .catch(() => undefined);
    }, [open, row, opportunityDetails]);

    const triggerErrorPopup = (content: string) => {
        Modal.error({ title: '', content, okText: 'OK', closable: false, centered: true });
    };

    const handleClose = () => {
        onCompleted(appId || undefined);
        onClose();
    };

    const addContact = () => {
        if (!selectedContact) {
            setShowCoApplicantTab(false);
            return;
        }
        const selected = nonPrimaryContacts.find((c) => Number(c.CustomerId) === Number(selectedContact));
        if (!selected) return;
        coForm.resetFields();
        coForm.setFieldsValue({
            grossMonthIncome: 0,
            uS_CitizenShip: 'true',
            phoneNumber: selected.Phone ? formatPhoneDigits(String(selected.Phone)) : '',
            phoneType: resolvePhoneTypeText(selected.PhoneType) || undefined,
        });
        const sp = primarySidebar.SalesPerson;
        setCoApplicantSidebar({
            OpportunityDetails: {
                Name: `${selected.FirstName ?? ''} ${selected.LastName ?? ''}`.trim(),
                Phone: selected.Phone,
                Email: selected.Email,
            },
            InstallationAddress: {
                Street: selected.InsStreet,
                City: selected.InsCity,
                State: formatAddrStateSegment(selected.InsState),
                ZipCode: selected.InsZipCode,
            },
            SalesPerson: sp,
        });
        setShowCoApplicantTab(true);
        setSelectedTabIndex(1);
    };

    const onDeleteCoApplicant = () => {
        Modal.confirm({
            title: '',
            content: 'Are you sure you want to delete this Co-Applicant?',
            okText: 'OK',
            cancelText: 'Cancel',
            closable: false,
            centered: true,
            onOk: () => {
                setSelectedTabIndex(0);
                setShowCoApplicantTab(false);
                setSelectedContact('');
                setCoApplicantSidebar(null);
                coForm.resetFields();
            },
        });
    };

    const mapApplicantPayload = (values: ApplicantValues, isPrimary: boolean, customerId: number) => ({
        ...values,
        dob: formatDate(values.dob),
        issueDate: formatDate(values.issueDate),
        expirationDate: formatDate(values.expirationDate),
        opportunityId: opportunityId != null && String(opportunityId).trim() !== '' ? Number(opportunityId) : null,
        salePersonEmail: salesPersonEmail || String(primarySidebar.SalesPerson?.Email || ''),
        salesAmount: parseMoney(row?.totalEpcDisplay),
        quoteId: row?.quoteIdRaw,
        mortgageRentalAmount: isPrimary ? values.mortgageRentalAmount ?? 0 : 0,
        grossMonthIncome: values.grossMonthIncome ?? 0,
        uS_CitizenShip: values.uS_CitizenShip === 'true',
        programType: programTypeValue,
        isPrimary,
        isCoApplicant: !isPrimary,
        customerId,
    });

    const handleSubmit = async () => {
        if (!acceptDeclaration) return;
        if (!programTypeValue) {
            triggerErrorPopup('No matching Program Type found. Please contact Administrator.');
            return;
        }
        try {
            const primary = await primaryForm.validateFields();
            let saveData: unknown[] = [mapApplicantPayload(primary, true, 0)];
            if (selectedContact) {
                const co = await coForm.validateFields();
                saveData = [saveData[0], mapApplicantPayload(co, false, Number(selectedContact))];
            }
            setLoading(true);
            const res: any = await contractService.submitServiceFinanceApplication(saveData);
            setLoading(false);
            if (res && res.IsSuccessful && res.Data) {
                setAppId(res.Data.AppId != null ? String(res.Data.AppId) : '');
            } else {
                triggerErrorPopup(res?.Message ? String(res.Message) : 'Submit was not successful.');
            }
        } catch (e: unknown) {
            setLoading(false);
            const err = e as { errorFields?: unknown; response?: { data?: { Message?: string; title?: string } }; message?: string };
            if (err?.errorFields) return;
            triggerErrorPopup(err?.response?.data?.Message || err?.response?.data?.title || err?.message || 'Submit failed.');
        }
    };

    const checkStatus = async () => {
        const oid = opportunityId;
        const cid = row?.opportunityContractIdRaw || row?.contractIdRaw;
        if (!oid || !cid) {
            triggerErrorPopup('Need opportunity id and contract id on this row.');
            return;
        }
        setLoading(true);
        try {
            const res = await contractService.getOpportunityContractStatus(oid as string | number, cid);
            const st =
                (res as { ContractStatus?: string })?.ContractStatus ??
                (res as { contractStatus?: string })?.contractStatus ??
                '';
            setModalTitle('Application Status');
            setShowCurrentApplicationStatus(true);
            setPrimaryContact(String(row?.primaryContactDisplay || primarySidebar.OpportunityDetails.Name || '').replace(/^—$/, ''));
            setContractStatus(String(st || row?.contractStatus || '').replace(/^—$/, ''));
        } catch (e: unknown) {
            const ax = e as { response?: { data?: { Message?: string } }; message?: string };
            triggerErrorPopup(ax?.response?.data?.Message || ax?.message || 'Unable to refresh application status.');
        } finally {
            setLoading(false);
        }
    };

    const primaryReady = applicantReady(primaryValues, true);
    const coReady = !showCoApplicantTab || applicantReady(coValues, false);
    const canSubmit = primaryReady && coReady && acceptDeclaration;

    const renderApplicantForm = (isCo: boolean) => {
        const form = isCo ? coForm : primaryForm;
        return (
            <Form
                form={form}
                layout="vertical"
                size="small"
                onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                }}
            >
                <div className="obd-sf-submit__form-grid">
                    <div>
                        <Form.Item label="Date of Birth" name="dob" rules={[{ required: true, message: 'Required' }]}>
                            <DatePicker format="MM/DD/YYYY" />
                        </Form.Item>
                        <SsnField name="ssn" show={isCo ? showSsnCo : showSsn} onToggle={() => (isCo ? setShowSsnCo((v) => !v) : setShowSsn((v) => !v))} />
                        <Form.Item label="Employer Name" name="employerName" rules={[{ required: true, message: 'Required' }]}>
                            <Input maxLength={50} />
                        </Form.Item>
                        <Form.Item label={<IncomeLabel />} name="grossMonthIncome" rules={[{ required: true, message: 'Required' }]}>
                            <InputNumber min={0} max={1000000} precision={2} prefix="$ " />
                        </Form.Item>
                        <Form.Item label="Residence" name="residence" rules={[{ required: true, message: 'Required' }]}>
                            <OBDSelect options={RESIDENCE} placeholder="Select" allowClear={false} />
                        </Form.Item>
                        {!isCo ? (
                            <Form.Item label="Mortgage/Rental Amount" name="mortgageRentalAmount" rules={[{ required: true, message: 'Required' }]}>
                                <InputNumber min={0} max={1000000} precision={2} prefix="$ " />
                            </Form.Item>
                        ) : null}
                        <Form.Item label="Property Type" name="propertyType" rules={[{ required: true, message: 'Required' }]}>
                            <OBDSelect options={PROPERTY_TYPES} placeholder="Select" allowClear={false} />
                        </Form.Item>
                        <Form.Item label="Phone Type" name="phoneType" rules={[{ required: true, message: 'Required' }]}>
                            <OBDSelect options={phoneTypeOptions} placeholder="Select" allowClear={false} />
                        </Form.Item>
                        {isCo ? (
                            <Form.Item
                                label="Phone Number"
                                name="phoneNumber"
                                getValueFromEvent={(e) => formatPhoneDigits(e.target.value)}
                                rules={[
                                    { required: true, message: 'Valid Phone Number is required' },
                                    { min: 14, message: 'Valid Phone Number is required' },
                                    { max: 14, message: 'Valid Phone Number is required' },
                                ]}
                            >
                                <Input maxLength={14} />
                            </Form.Item>
                        ) : null}
                    </div>
                    <div>
                        <Form.Item label="Identify Document Type" name="iD_Type" rules={[{ required: true, message: 'Required' }]}>
                            <OBDSelect options={DOCUMENT_TYPES} placeholder="Select" allowClear={false} />
                        </Form.Item>
                        <Form.Item label="Document Number" name="documentNumber" rules={[{ required: true, message: 'Required' }]}>
                            <Input maxLength={25} />
                        </Form.Item>
                        <Form.Item label="Document State" name="state" rules={[{ required: true, message: 'Required' }]}>
                            <Input maxLength={25} />
                        </Form.Item>
                        <Form.Item label="Document Issue Date" name="issueDate" rules={[{ required: true, message: 'Required' }]}>
                            <DatePicker format="MM/DD/YYYY" />
                        </Form.Item>
                        <Form.Item label="Document Expiration Date" name="expirationDate" rules={[{ required: true, message: 'Required' }]}>
                            <DatePicker format="MM/DD/YYYY" />
                        </Form.Item>
                        <Form.Item label="Document Issuer" name="issuer" rules={[{ required: true, message: 'Required' }]}>
                            <Input maxLength={50} />
                        </Form.Item>
                        <Form.Item label="US Citizen" name="uS_CitizenShip" rules={[{ required: true, message: 'Required' }]}>
                            <OBDSelect options={CITIZEN} allowClear={false} />
                        </Form.Item>
                        {!isCo ? (
                            <Form.Item
                                label="Phone Number"
                                name="phoneNumber"
                                getValueFromEvent={(e) => formatPhoneDigits(e.target.value)}
                                rules={[
                                    { required: true, message: 'Valid Phone Number is required' },
                                    { min: 14, message: 'Valid Phone Number is required' },
                                    { max: 14, message: 'Valid Phone Number is required' },
                                ]}
                            >
                                <Input maxLength={14} />
                            </Form.Item>
                        ) : null}
                    </div>
                </div>
            </Form>
        );
    };

    const tabItems = [
        { key: '0', label: 'Primary Applicant', children: null },
        ...(showCoApplicantTab
            ? [
                  {
                      key: '1',
                      label: (
                          <span className="obd-sf-submit__co-tab">
                              Co Applicant
                              <button
                                  type="button"
                                  className="obd-sf-submit__trash"
                                  title="Delete Contact"
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteCoApplicant();
                                  }}
                              >
                                  <Trash size={14} />
                              </button>
                          </span>
                      ),
                      children: null,
                  },
              ]
            : []),
    ];

    return (
        <DashboardModal
            open={open && !!row}
            title={modalTitle}
            onClose={handleClose}
            theme={modalTheme}
            footer={
                !appId ? (
                    <Button type="primary" onClick={() => void handleSubmit()} disabled={!canSubmit} loading={loading}>
                        Submit
                    </Button>
                ) : null
            }
            width={800}
            destroyOnClose
            maskClosable={false}
            loading={loading}
            className="obd-sf-submit-modal"
            rootClassName={`obd-form-controls-modal-root obd-theme-${modalTheme}`}
        >
            {!row ? null : appId && !showCurrentApplicationStatus ? (
                <div className="obd-sf-submit__post">
                    <p>Thank you!</p>
                    <p>Your application has been submitted.</p>
                    <p>Please click the &quot;Check Status&quot; button below to check the current status of this application.</p>
                    <Button type="primary" onClick={() => void checkStatus()}>
                        Check Status
                    </Button>
                </div>
            ) : appId && showCurrentApplicationStatus ? (
                <div className="obd-sf-submit__post">
                    <p>
                        Application for {primaryContact} - {contractStatus}
                    </p>
                </div>
            ) : (
                <div className="obd-sf-submit__body">
                    <Tabs
                        className="obd-sf-submit__tabs"
                        activeKey={String(selectedTabIndex)}
                        items={tabItems}
                        onChange={(key) => setSelectedTabIndex(Number(key))}
                    />
                    <div className="obd-sf-submit__layout">
                        <div>
                            <SidebarBlock info={sidebar} />
                            {selectedTabIndex === 0 ? (
                                <div className="obd-sf-submit__contacts">
                                    <label className="obd-sf-submit__contacts-label" htmlFor="obd-sf-contact">
                                        Contacts
                                    </label>
                                    <div className="obd-sf-submit__contacts-row">
                                        <OBDSelect
                                            id="obd-sf-contact"
                                            style={{ flex: 1 }}
                                            placeholder="Select"
                                            value={selectedContact || undefined}
                                            onChange={(v) => setSelectedContact(String(v ?? ''))}
                                            options={nonPrimaryContacts.map((c) => ({
                                                value: String(c.CustomerId ?? ''),
                                                label: `${c.FirstName ?? ''} ${c.LastName ?? ''}`.trim(),
                                            }))}
                                        />
                                        <button type="button" className="obd-sf-submit__plus" title="Add Contact" onClick={addContact}>
                                            +
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        <div className="obd-sf-submit__main">
                            <span className="obd-sf-submit__section-title">Application Details</span>
                            {selectedTabIndex === 0 ? renderApplicantForm(false) : renderApplicantForm(true)}
                        </div>
                    </div>
                    <div className="obd-sf-submit__disclosures">
                        <Checkbox checked={acceptDeclaration} onChange={(e) => setAcceptDeclaration(e.target.checked)}>
                            The Undersigned hereby represents and agrees that:
                        </Checkbox>
                        <div className="obd-sf-submit__disclosures-box">
                            <label className="obd-sf-submit__disclosures-intro">
                                Please read the disclosures and acknowledge you have read the disclosures by checking the checkbox.
                            </label>
                            {DISCLOSURE_POINTS.map((html, i) => (
                                <label key={i} dangerouslySetInnerHTML={{ __html: html }} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </DashboardModal>
    );
}

export default ServiceFinanceSubmitModal;
